import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Loader2, ArrowRight, RefreshCw, ShieldCheck } from "lucide-react";
import { createLead, getCaptcha } from "../lib/api";
import { INTENT_OPTIONS, BUDGET_OPTIONS, TIMELINE_OPTIONS, US_STATES, EXCLUDED_STATES } from "../data";

const EMPTY = { name: "", email: "", phone: "", intent: "Sell My Land", state: "", budget: "", timeline: "", message: "" };

export const LeadForm = ({ variant = "glass", title, subtitle }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [captcha, setCaptcha] = useState({ question: "", token: "" });
  const [captchaAnswer, setCaptchaAnswer] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const glass = variant === "glass";

  const loadCaptcha = async () => {
    try {
      const res = await getCaptcha();
      setCaptcha(res.data);
      setCaptchaAnswer("");
    } catch {
      setCaptcha({ question: "", token: "" });
    }
  };

  useEffect(() => {
    loadCaptcha();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    if (!captchaAnswer.trim()) {
      toast.error("Please answer the verification question.");
      return;
    }
    if (EXCLUDED_STATES.includes(form.state)) {
      toast.info(
        `We're not currently buying vacant land in ${form.state} yet, so we're unable to accept this submission. We're expanding fast — please check back soon. Thank you for thinking of us!`,
        { duration: 8000 }
      );
      return;
    }
    setLoading(true);
    try {
      await createLead({
        ...form,
        captcha_token: captcha.token,
        captcha_answer: captchaAnswer,
        website: honeypot,
      });
      toast.success("Thank you! We'll reach out within one business day.");
      setForm(EMPTY);
      loadCaptcha();
    } catch (err) {
      const msg = err?.response?.data?.detail;
      toast.error(msg === "Captcha verification failed" ? "Verification failed — please try the new question." : "Something went wrong. Please call us directly.");
      loadCaptcha();
    } finally {
      setLoading(false);
    }
  };

  const inputClass = glass
    ? "glass-input w-full px-4 py-3 text-sm"
    : "w-full px-4 py-3 text-sm bg-sand-200 border border-forest/15 text-ink focus:outline-none focus:border-terracotta transition-colors duration-300";
  const labelClass = glass
    ? "text-[11px] uppercase tracking-[0.18em] text-sand/70 mb-1.5 block"
    : "text-[11px] uppercase tracking-[0.18em] text-forest/60 mb-1.5 block";

  return (
    <form
      onSubmit={submit}
      data-testid="lead-form"
      className={glass ? "glass-form p-6 md:p-8" : "bg-white border border-forest/10 p-6 md:p-9 shadow-xl"}
    >
      {title && (
        <div className="mb-5">
          <h3 className={`font-display text-2xl md:text-3xl ${glass ? "text-sand" : "text-forest"}`}>{title}</h3>
          {subtitle && <p className={`text-sm mt-1 ${glass ? "text-sand/60" : "text-forest/60"}`}>{subtitle}</p>}
        </div>
      )}

      <div className="grid gap-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Name</label>
            <input data-testid="lead-name" className={inputClass} value={form.name} onChange={set("name")} placeholder="Full name" />
          </div>
          <div>
            <label className={labelClass}>Phone</label>
            <input data-testid="lead-phone" className={inputClass} value={form.phone} onChange={set("phone")} placeholder="(941) 000-0000" />
          </div>
        </div>

        <div>
          <label className={labelClass}>Email</label>
          <input data-testid="lead-email" type="email" className={inputClass} value={form.email} onChange={set("email")} placeholder="you@email.com" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>I'm looking to…</label>
            <select data-testid="lead-intent" className={inputClass} value={form.intent} onChange={set("intent")}>
              {INTENT_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Budget / Asking Price</label>
            <select data-testid="lead-budget" className={inputClass} value={form.budget} onChange={set("budget")}>
              <option value="">Select…</option>
              {BUDGET_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Property State</label>
            <select data-testid="lead-state" className={inputClass} value={form.state} onChange={set("state")}>
              <option value="">Select state…</option>
              {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Timeline</label>
            <select data-testid="lead-timeline" className={inputClass} value={form.timeline} onChange={set("timeline")}>
              <option value="">Select…</option>
              {TIMELINE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className={labelClass}>Message</label>
          <textarea data-testid="lead-message" rows={3} className={inputClass} value={form.message} onChange={set("message")} placeholder="Tell us about the lot you want or the lot you're selling…" />
        </div>

        {/* Honeypot — hidden from humans, catches bots */}
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
          data-testid="lead-honeypot"
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
        />

        <div>
          <label className={`${labelClass} flex items-center gap-1.5`}>
            <ShieldCheck size={13} className="text-terracotta" /> Quick check — what is {captcha.question || "…"}?
          </label>
          <div className="flex items-center gap-2">
            <input
              data-testid="lead-captcha"
              inputMode="numeric"
              className={inputClass}
              value={captchaAnswer}
              onChange={(e) => setCaptchaAnswer(e.target.value)}
              placeholder="Answer"
            />
            <button
              type="button"
              data-testid="lead-captcha-refresh"
              onClick={loadCaptcha}
              aria-label="New question"
              className={`shrink-0 p-3 border transition-colors duration-300 ${glass ? "border-white/15 text-sand/70 hover:text-sand hover:border-white/40" : "border-forest/20 text-forest/60 hover:text-forest hover:border-forest/40"}`}
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </div>

        <button
          type="submit"
          data-testid="lead-submit"
          disabled={loading}
          className="group mt-1 flex items-center justify-center gap-2 bg-terracotta text-white px-6 py-3.5 tracking-wide hover:bg-terracotta-dark transition-colors duration-300 disabled:opacity-60"
        >
          {loading ? <Loader2 size={18} className="animate-spin" /> : <>Get a Cash Offer <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" /></>}
        </button>
      </div>
    </form>
  );
};
